import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import useMessage from '../lib/useMessage';
import InlineMessage from '../components/InlineMessage';
import { isBranchScopedManager, isAdminOrExecutive, canGrantRequestApproval, floorSwitchesApplyTo, canChangePerson, canSetPersonActive } from '../lib/permissions';
import { passwordRedirectUrl } from '../lib/appUrl';
import StationGovernance from '../components/settings/StationGovernance';
import RemoveUserSheet from '../components/settings/RemoveUserSheet';

export function UsersPage() {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });
  const canManagePermissionToggles = isAdminOrExecutive({ isAdmin, isExecutive });
  // #811, Session 296. The person looking at the list, in the shape the per-row
  // predicates take — profile included, because canChangePerson() reads it.
  const viewer = { isAdmin, isManager, isExecutive, profile };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allStations, setAllStations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // #404, Session 78n6. When an admin edits an existing person and changes the
  // address, the save stops here first. The address is the one field on this
  // form that changes how somebody signs in, so it gets a confirmation naming
  // the consequence rather than the operation — gate callout A.
  const [emailConfirm, setEmailConfirm] = useState(null);
  // #381, Session 78n6. The Remove control, and the third state it introduces.
  // Archived is not a second meaning for Inactive -- Inactive already means
  // something real and useful ("on the team, not currently working") and
  // overloading it would lose a distinction there is no way back from. D3.
  const [removeTarget, setRemoveTarget] = useState(null);
  const [tab, setTab] = useState('active');
  const [archiveEvents, setArchiveEvents] = useState({});
  const isMobile = window.innerWidth <= 768;
  const userMsg = useMessage();

  const ROLE_BADGE = {
    admin: 'badge-approved',
    executive: 'badge-approved',
    manager: 'badge-atshop',
    team_member: 'badge-spare',
    purchasing: 'badge-spare',
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [
      { data: usersData },
      { data: branchData },
      { data: deptData },
      { data: stationData },
      { data: logData },
    ] = await Promise.all([
      supabase.from('users').select('*, branches(name)').order('full_name'),
      supabase.from('branches').select('*'),
      supabase.from('departments').select('*').eq('is_active', true).order('name'),
      supabase.from('branch_stations').select('*').eq('is_active', true).order('name'),
      // #381 -- how many records still carry an archived person's name, and who
      // archived them. Read from the log rather than recounted, because the
      // point of the record is what was true at the time. Gate callout I.
      supabase.from('user_lifecycle_log')
        .select('target_user_id, attached_total, performed_at, performed_by_name_snapshot')
        .eq('action', 'archived')
        .order('performed_at', { ascending: false }),
    ]);
    const events = {};
    for (const e of (logData || [])) {
      // Ordered newest first, so the first one seen for a person is the
      // archiving that currently stands.
      if (e.target_user_id && !events[e.target_user_id]) events[e.target_user_id] = e;
    }
    setArchiveEvents(events);
    setUsers(usersData || []);
    setBranches(branchData || []);
    setDepartments(deptData || []);
    setAllStations(stationData || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // #272, Session 78n4. The department select now stores a real answer rather
  // than an absence: 'branch' for a branch manager, or a department's id.
  // `selectedDeptName` translates that back into the department NAME, which two
  // things still need — the station list below, and the `users.department` text
  // column that Tools, Inspections and Tag-outs read. Converting those screens
  // belongs to the Tools sprint that owns them, so the name keeps being written
  // alongside the id and cannot drift from it, because both come from here.
  const selectedDeptName =
    departments.find(d => String(d.id) === String(form.department_choice))?.name || '';

  const availableStations = allStations.filter(s =>
    String(s.branch_id) === String(form.branch_id) &&
    s.department === selectedDeptName
  );

  // #381, D3. Three states, three tabs, and the counts are on the tabs.
  // Archived people are not in the main list and are not counted in it -- the
  // tab is how you reach them, because "who was that" is a real question and a
  // name in an old record should be findable.
  const archivedUsers = users.filter(u => u.archived_at);
  const liveUsers = users.filter(u => !u.archived_at);
  const activeUsers = liveUsers.filter(u => u.is_active);
  const inactiveUsers = liveUsers.filter(u => !u.is_active);
  const visibleUsers =
    tab === 'archived' ? archivedUsers
    : tab === 'inactive' ? inactiveUsers
    : activeUsers;

  function openAdd() {
    setEditUser(null);
    setForm({
      full_name: '',
      email: '',
      role: 'team_member',
      branch_id: isBranchScoped ? profile?.branch_id : '',
      department_choice: '',
      station_truck: '',
      is_active: true,
      can_approve_requests: false,
      // Defined even though its checkbox does not render on Add: an undefined
      // `checked` flips React from uncontrolled to controlled the moment
      // anything sets it, and warns.
      production_signoff_enabled: false,
      // #699, gate 665 D28 -- the same reason: defined, though editing only.
      drives_trucks: false,
      // #746 D4, Session 79.22b.6.4.1 -- and again: "Can record welds".
      weld_record_enabled: false,
    });
    userMsg.clearMessage();
    setEmailConfirm(null);
    setShowModal(true);
  }

  function openEdit(u) {
    setEditUser(u);
    setForm({
      full_name: u.full_name || '',
      email: u.email || '',
      role: u.role || 'team_member',
      branch_id: u.branch_id || '',
      // 'branch' means branch manager, otherwise the department's id as a string.
      // The `u.department` name lookup is a fallback for a record whose id somehow
      // never got linked; after the #272 migration nobody should hit it, and if
      // anybody does the select shows their real department rather than blank.
      department_choice:
        u.manager_scope === 'branch' ? 'branch'
        : u.department_id ? String(u.department_id)
        : (departments.find(d => d.name === u.department)?.id
            ? String(departments.find(d => d.name === u.department).id)
            : ''),
      station_truck: u.station_truck || '',
      is_active: u.is_active,
      can_approve_requests: u.can_approve_requests === true,
      purchasing_actions_enabled: u.purchasing_actions_enabled !== false,
      vendor_catalog_actions_enabled: u.vendor_catalog_actions_enabled !== false,
      invoice_reconciliation_enabled: u.invoice_reconciliation_enabled !== false,
      // #422/78t -- opt-OUT, so an unset column reads as ON. Matches
      // purchasing_actions_enabled rather than the two `=== true` toggles
      // above it: Colby's ruling is that branch managers hold this unless
      // somebody takes it away.
      catalog_actions_enabled: u.catalog_actions_enabled !== false,
      // #515/79.8 -- opt-IN (`=== true`), matching the two money-path toggles
      // above rather than the catalog one below them. The column is NOT NULL
      // DEFAULT true so a real row reads true; `=== true` is what makes a
      // profile that has not loaded fail CLOSED, which is the only correct
      // direction on something that revalues stock.
      production_cost_actions_enabled: u.production_cost_actions_enabled === true,
      // #553 D5, Session 79.15e. Opt-IN (`=== true`), and the column is NOT
      // NULL DEFAULT false, so a profile that has not loaded fails CLOSED.
      production_signoff_enabled: u.production_signoff_enabled === true,
      // #699, gate 665 D28, Session 79.22b.5.1. `=== true`, NOT NULL DEFAULT
      // false: a profile that has not loaded reads as not a driver.
      drives_trucks: u.drives_trucks === true,
      // #746 D4, Session 79.22b.6.4.1. Opt-IN (`=== true`), NOT NULL DEFAULT
      // false, as sign-off is: a profile that has not loaded reads as off.
      weld_record_enabled: u.weld_record_enabled === true,
    });
    userMsg.clearMessage();
    setEmailConfirm(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEmailConfirm(null);
  }

  // #404, Session 78n6. Changing an address touches the sign-in record as well
  // as the profile, which needs the service role, which the browser must never
  // hold — so it goes through a Pages Function. See functions/update-user-email.js.
  //
  // Returns null on success and an error string otherwise, so the caller can
  // abandon the whole save rather than half-applying it.
  async function applyEmailChange(userId, nextEmail) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) return 'Your session has expired — please sign in again.';

      const res = await fetch('/update-user-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ user_id: userId, email: nextEmail }),
      });

      // #377's shape, handled at this call site rather than left to say
      // "Network error". Under `npm start` CRA serves index.html for an unknown
      // path, so res.json() throws on a page of HTML and the real cause — the
      // function simply is not served in local dev — reads as the app being
      // broken. The message says which it is.
      const contentType = res.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        return 'Email changes need the deployed site — this service is not served by the local dev server.';
      }

      const result = await res.json();
      if (!res.ok) return result.error || 'Could not change the email address.';
      return null;
    } catch {
      return 'Network error — could not reach the email service.';
    }
  }

  async function saveUser() {
    // #119 — cleared on open only, so a corrected resubmission within the same
    // modal session kept the original error on screen.
    userMsg.clearMessage();
    if (!form.full_name?.trim()) { userMsg.showError('Full name is required.'); return; }
    if (!form.email?.trim()) { userMsg.showError('Email is required.'); return; }
    if (!form.branch_id && !['executive', 'purchasing'].includes(form.role)) {
      userMsg.showError('Branch is required.'); return;
    }
    // #272, Session 78n4. A manager with nothing chosen here is the exact
    // ambiguity this work exists to end — "branch-wide on purpose" and "nobody
    // finished filling this in" used to be the same blank box, and the wider
    // authority was the one that won. Making it required is the whole fix.
    if (form.role === 'manager' && !form.department_choice) {
      userMsg.showError('Choose a department, or "All departments (branch manager)". This decides which requests they approve.');
      return;
    }
    // 'branch' is a scope, not a department, so it stores no department id.
    const isDeptRole = ['manager', 'team_member'].includes(form.role);
    const chosenDeptId =
      isDeptRole && form.department_choice && form.department_choice !== 'branch'
        ? Number(form.department_choice)
        : null;

    // #404, Session 78n6. The address is the one field on this form that changes
    // how somebody signs in, so a change to it stops for confirmation before
    // anything at all is written — including the other fields on the form. If
    // the admin backs out, nothing has happened yet.
    if (editUser) {
      const nextEmail = form.email.trim().toLowerCase();
      const currentEmail = (editUser.email || '').trim().toLowerCase();
      if (nextEmail !== currentEmail && !emailConfirm) {
        setEmailConfirm({
          from: editUser.email || '',
          to: nextEmail,
          name: (form.full_name.trim() || editUser.full_name || 'This person').split(' ')[0],
        });
        return;
      }
    }

    setSaving(true);

    if (editUser) {
      // The address goes first and the rest of the form waits on it. A duplicate
      // address is the likely failure and it must not leave the other edits
      // applied against a person whose email was refused.
      if (emailConfirm) {
        const emailErr = await applyEmailChange(editUser.id, emailConfirm.to);
        if (emailErr) {
          userMsg.showError(emailErr);
          setEmailConfirm(null);
          setSaving(false);
          return;
        }
      }
      const { error } = await supabase.from('users').update({
        full_name: form.full_name.trim(),
        role: form.role,
        branch_id: form.branch_id || null,
        // #272/#396, Session 78n4. Three columns written from one control.
        //   department_id  — the real link, and what approvals route on.
        //   department     — the same department's NAME, still read by Tools,
        //                    Inspections and Tag-outs. Written from the same
        //                    choice so the two cannot drift; the Tools sprint
        //                    owns converting those screens off it.
        //   manager_scope  — 'branch' or 'department', a stated fact rather than
        //                    something inferred from an empty box.
        department_id: isDeptRole ? chosenDeptId : null,
        department: isDeptRole ? (selectedDeptName || null) : null,
        manager_scope: form.role === 'manager'
          ? (form.department_choice === 'branch' ? 'branch' : 'department')
          : null,
        station_truck: form.role === 'team_member' ? (form.station_truck || null) : null,
        is_active: form.is_active,
        // The approval switch is only written by somebody entitled to move it.
        // A branch manager is always an approver structurally, so their row is
        // kept true rather than left to whatever the checkbox happened to show.
        //
        // #496, Session 79.1 — AND IT IS CLEARED WHEN THE ROLE STOPS BEING A
        // MANAGER'S, which is the half that was missing.
        //
        // What went wrong. An administrator changes a department manager to a
        // field member. The role is written, manager_scope is correctly cleared —
        // and can_approve_requests was left at true, because it was spread in only
        // while the new role was `manager`. Observed live at Session 79: role
        // team_member, manager_scope null, can_approve_requests still true.
        //
        // Nothing was exploitable, which is what made it easy to miss:
        // isManagerApproverFor() returns false for any non-manager role, so the
        // flag did nothing. THE DANGER IS ON THE WAY BACK. Promote that person to
        // department manager again — a supervisor steps down for a season and
        // returns, which is ordinary at a sign company — and they regain authority
        // to approve their department's spending with nobody granting it, while
        // the administrator doing the promotion sees a box that was already
        // ticked when they arrived.
        //
        // The neighbouring field already guards this exact hazard and says so:
        // catalog_actions_enabled is written for EVERY manager rather than only
        // while its checkbox renders, "because promoting a department manager to
        // branch manager later would hand them whatever value happened to be
        // stored from before". This is that shape with the opposite direction.
        //
        // CLEARING IS NOT GATED ON canGrantApproval, deliberately. Losing the
        // authority is a CONSEQUENCE of the role change, not a grant — gating it
        // would let an editor who can change the role but not the switch leave a
        // stale true behind, which is the defect again wearing a permission check.
        // Re-granting stays gated, because that is a real decision by somebody
        // entitled to make it.
        ...(form.role === 'manager'
          ? (canGrantApproval ? {
              can_approve_requests: form.department_choice === 'branch' ? true : !!form.can_approve_requests,
            } : {})
          : { can_approve_requests: false }),
        // Purchasing-queue access stays manager-only: canActionPurchasingQueue
        // grants isPurchasing unconditionally and that is correct, because
        // working the queue is the purchasing role's whole job.
        ...(form.role === 'manager' && canManagePermissionToggles ? {
          purchasing_actions_enabled: form.purchasing_actions_enabled,
          // #422/78t. Written for EVERY manager, not only branch managers,
          // even though the predicate reads it only for branch ones. If it
          // were written only while the checkbox renders, promoting a
          // department manager to branch manager later would hand them
          // whatever value happened to be stored from before -- which is
          // #289's defect exactly: a column set, ignored, and unreachable.
          catalog_actions_enabled: form.catalog_actions_enabled,
          // #515/79.8. Written for every manager for the same reason as the
          // line above: writing it only while its checkbox renders means a
          // later role change hands somebody whatever value happened to be
          // stored from before.
          production_cost_actions_enabled: form.production_cost_actions_enabled,
        } : {}),
        // #553 D5, Session 79.15e -- WRITTEN FOR EVERY ROLE, not only while its
        // checkbox renders, and that is the hazard the two toggles above spell
        // out in full rather than a style choice.
        //
        // The box shows for a team member, because a manager already qualifies
        // by role and a checkbox that changes nothing for the person in front of
        // you reads as governance without being it. But a manager who steps down
        // to the floor -- ordinary at a sign company -- would then inherit
        // whatever was stored months ago, with the administrator doing the
        // demotion seeing no box at all. Writing the round-tripped form value on
        // every save means what is stored is always what somebody last chose.
        ...(canManagePermissionToggles ? {
          production_signoff_enabled: form.production_signoff_enabled,
          // #746 D4, Session 79.22b.6.4.1 -- "Can record welds", the same shape
          // as sign-off on the line above and for the same reason: written on
          // every save, so a role change never hands somebody a value stored
          // months ago with no box on screen to show it. Only an admin or an
          // executive moves it here. That is the screen's half only - the
          // database does not yet refuse anybody else (#798).
          weld_record_enabled: !!form.weld_record_enabled,
        } : {}),
        // #699, gate 665 D28, Session 79.22b.5.1 -- "Drives trucks for
        // transfers". NOT behind canManagePermissionToggles, because it is not a
        // permission: it grants nothing, it only puts a person first on the
        // driver list and decides which tab is their job. Whoever may edit the
        // person may say they drive. WRITTEN ON EVERY SAVE, for #553 D5's
        // reason above: a stored value a later role change inherits unseen is
        // the defect, so what is stored is always what somebody last chose.
        drives_trucks: !!form.drives_trucks,
        // #289, Session 74z2 — vendor_catalog_actions_enabled moved into this
        // block alongside reconcile. It was written only for managers, so after
        // the toggle started rendering for purchasing users it would have shown
        // their change, saved, and silently persisted nothing. Caught by reading
        // this write path rather than by the UI, which looked correct either way.
        ...(['manager', 'purchasing'].includes(form.role) && canManagePermissionToggles ? {
          vendor_catalog_actions_enabled: form.vendor_catalog_actions_enabled,
          invoice_reconciliation_enabled: form.invoice_reconciliation_enabled,
        } : {}),
      }).eq('id', editUser.id);
      if (error) { userMsg.showError('Error updating user: ' + error.message); setSaving(false); return; }
      const changedEmailTo = emailConfirm?.to;
      setEmailConfirm(null);
      setSaving(false);
      setShowModal(false);
      userMsg.showSuccess(
        changedEmailTo
          ? `${form.full_name.trim()} updated. They sign in with ${changedEmailTo} from now on.`
          : `${form.full_name.trim()} updated.`
      );
      load();
      return;
    }

    // New user — call Cloudflare Pages Function
    let emailWarning = false;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        userMsg.showError('Your session has expired — please sign in again.');
        setSaving(false);
        return;
      }

      const res = await fetch('/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          role: form.role,
          branch_id: form.branch_id || null,
          department: isDeptRole ? (selectedDeptName || null) : null,
          department_id: isDeptRole ? chosenDeptId : null,
          manager_scope: form.role === 'manager'
            ? (form.department_choice === 'branch' ? 'branch' : 'department')
            : null,
          station_truck: form.role === 'team_member' ? (form.station_truck || null) : null,
          // #490, Session 79. Sent only when this caller is actually entitled to
          // move it, mirroring the edit path's own guard rather than trusting
          // the checkbox's last rendered state. A branch manager is left out
          // entirely: the server sets them true structurally, and sending a
          // value for them would invite the client to argue about a fact the
          // rule says is not switchable.
          //
          // This is the CLIENT half only. The server re-derives the same
          // entitlement from the caller's own row — a payload is a request,
          // never a permission, and anyone can post to this endpoint with a
          // valid token.
          ...(form.role === 'manager'
            && form.department_choice !== 'branch'
            && canGrantApproval
            ? { can_approve_requests: !!form.can_approve_requests }
            : {}),
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        userMsg.showError(result.error || 'Failed to create user.');
        setSaving(false);
        return;
      }
      if (result.emailSent === false) {
        userMsg.showError(`Account created, but the invite email failed to send. Have ${form.email.trim()} use "Forgot password" on the login screen instead.`);
        emailWarning = true;
      }
    } catch (err) {
      userMsg.showError('Network error — could not reach invite service.');
      setSaving(false);
      return;
    }

    setSaving(false);
    setShowModal(false);
    if (!emailWarning) userMsg.showSuccess(`Invite sent to ${form.email.trim()}.`);
    load();
  }

  async function toggleActive(u) {
    // #811, Session 293. The database refuses a press from someone who may not
    // change this person (#798's guard); the refusal is said, never swallowed.
    //
    // #268's sign-out half, Session 296 — through functions/set-user-active.js
    // now, so the sign-in is locked (or unlocked) with the switch. The function
    // writes the change as the person pressing, so the guard still decides, and
    // its sentence still reaches this screen.
    const verb = u.is_active ? 'deactivated' : 'reactivated';
    let result;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) { userMsg.showError('Your session has expired — please sign in again.'); return; }
      const res = await fetch('/set-user-active', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ user_id: u.id, active: !u.is_active }),
      });
      // #377's shape: under `npm start` an unknown path returns index.html, so
      // say which it is rather than reading as a broken app.
      const contentType = res.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        userMsg.showError('Deactivating and reactivating people needs the deployed site — this service is not served by the local dev server.');
        return;
      }
      result = await res.json();
      if (!res.ok) {
        userMsg.showError(`${u.full_name} was not ${verb}: ${result.error || 'the change was refused.'}`);
        return;
      }
    } catch {
      userMsg.showError(`${u.full_name} was not ${verb}: the service could not be reached.`);
      return;
    }
    // Deactivated, but the lock at Supabase failed: said, never swallowed.
    if (result.warning) userMsg.showError(`${u.full_name} is now inactive, but ${result.warning}`);
    load();
  }

  async function sendPasswordLink(u) {
    // Issue #263 — the origin this page is served from, not a build-time variable.
    const { error } = await supabase.auth.resetPasswordForEmail(u.email, {
      redirectTo: passwordRedirectUrl(),
    });
    if (error) {
      userMsg.showError('Failed to send password link: ' + error.message);
      return;
    }
    userMsg.showSuccess(`Password link sent to ${u.email}`);
  }

  const f = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      // clear station when branch or department changes
      if (k === 'branch_id' || k === 'department_choice') next.station_truck = '';
      // A branch manager's approval is structural and cannot be switched off
      // (canApprove reads manager_scope, never the switch), so the checkbox
      // follows the scope rather than sitting there looking editable. #396 D5.
      if (k === 'department_choice' && v === 'branch') next.can_approve_requests = true;
      return next;
    });
  };

  const ROLE_CHOICES = [
    { value: 'team_member', label: 'Team member' },
    { value: 'manager', label: 'Manager' },
    { value: 'executive', label: 'Executive' },
    { value: 'purchasing', label: 'Purchasing' },
    { value: 'admin', label: 'Admin' },
  ];
  // #811, Session 296 (amended at Session 294's walk; the approved drawing, R2).
  // Anyone but an admin may set Team member only, and the box used to offer
  // exactly that — so for Dana Wells, a department manager, it READ "Team
  // member": the sheet kept her real role behind the box and saved it, while
  // telling the branch manager the wrong thing about her. The person's own role
  // now leads the list, so the box says what they are, and Team member follows
  // as the one thing the viewer may change it to (#798's guard allows a manager
  // to move someone below them down to team member, and nowhere else).
  const currentRoleChoice = !isAdmin && editUser && editUser.role !== 'team_member'
    ? ROLE_CHOICES.find(r => r.value === editUser.role)
    : null;
  const availableRoles = isAdmin
    ? ROLE_CHOICES
    : [...(currentRoleChoice ? [currentRoleChoice] : []), ROLE_CHOICES[0]];

  // #811 -- the branch a manager's line above the list names.
  const ownBranchName =
    branches.find(b => String(b.id) === String(profile?.branch_id))?.name || 'your branch';

  const availableBranches = isAdmin
    ? branches
    : branches.filter(b => String(b.id) === String(profile?.branch_id));

  const showDepartment = ['manager', 'team_member'].includes(form.role);
  const showStation = form.role === 'team_member' && form.department_choice && availableStations.length > 0;
  // #746 D4, Session 79.22b.6.4.1. The form's answer to "what kind of manager",
  // in the words floorSwitchesApplyTo() takes: 'branch' for a branch manager,
  // 'department' once a department is chosen, nothing while it is unset.
  const formScope = form.department_choice === 'branch' ? 'branch'
    : form.department_choice ? 'department' : null;

  // #396, Session 78n4. Who may move this person's approval switch.
  // Admin and executive anywhere; a branch manager for the department managers
  // at their own branch — Colby's instruction at this session's open.
  //
  // #490, Session 79. On the INVITE form there is no `editUser`, because the
  // person does not exist yet — and `canGrantRequestApproval` returns false the
  // moment its target is missing. That is right for a real absent record and
  // wrong here, where the target is simply not saved yet: it made the switch
  // unavailable to a branch manager inviting one of their own department
  // managers, which is exactly the grant the rule is written to allow.
  //
  // So the rule is asked about the person the form is DESCRIBING. The predicate
  // is not re-implemented and no second rule is introduced — locked rule 8 —
  // it is handed a prospective row shaped like the one the invite will create.
  // `id` is null deliberately, so the "cannot grant to yourself" test still
  // behaves: nobody's own id is null.
  const approvalTarget = editUser || (form.role === 'manager' ? {
    id: null,
    role: 'manager',
    manager_scope: form.department_choice === 'branch' ? 'branch' : 'department',
    branch_id: form.branch_id || null,
  } : null);
  const canGrantApproval = canGrantRequestApproval(
    { isAdmin, isManager, isExecutive, profile },
    approvalTarget
  );

  // Only admin and executive may make somebody a BRANCH manager. A branch
  // manager may grant approval downward to their department managers, but must
  // not be able to promote one into a peer with authority over the whole branch
  // — that is granting authority sideways, not delegating it downward. Without
  // this the department select would have become a promotion control.
  const canSetBranchScope = isAdminOrExecutive({ isAdmin, isExecutive });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">User management</div>
          <div className="page-sub">
            {liveUsers.length} {liveUsers.length === 1 ? 'person' : 'people'} · manage system access and roles
          </div>
        </div>
        {(isAdmin || isManager) && (
          <button className="btn btn-primary" onClick={openAdd}>+ Add user</button>
        )}
      </div>

      <InlineMessage type={userMsg.type} message={userMsg.message} onDismiss={userMsg.clearMessage} />

      {/* #381, D3. The counts live on the tabs, so the shape of the company is
          readable without opening anything. Archived is a real third state
          rather than a second meaning for Inactive. */}
      {!loading && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { key: 'active', label: 'Active', n: activeUsers.length },
            { key: 'inactive', label: 'Inactive', n: inactiveUsers.length },
            { key: 'archived', label: 'Archived', n: archivedUsers.length },
          ].map(t => (
            <button
              key={t.key}
              className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setTab(t.key)}
            >
              {t.label} · {t.n}
            </button>
          ))}
        </div>
      )}

      {/* #811, Session 296 (the approved drawing, R2). A manager sees Edit and
          Deactivate only beside the people below them at their own branch, so
          the list says why the other rows carry neither. An admin or an
          executive changes anyone and needs no line. */}
      {!loading && isBranchScoped && (
        <div className="text-muted" style={{ fontSize: 13, margin: '-4px 0 10px' }}>
          You can edit and deactivate the people below you at {ownBranchName}.
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner" />Loading...</div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Branch</th>
                  <th>Dept / Station</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7">
                      <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <div className="empty-state-title">
                          {tab === 'archived' ? 'Nobody is archived'
                            : tab === 'inactive' ? 'Nobody is inactive'
                            : 'No users yet'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : visibleUsers.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.full_name}</strong></td>
                    <td className="text-muted">{u.email}</td>
                    {/* #480, Speak Up OOB-6. TWO INDEPENDENT SIGNALS, and the
                        independence is the design rather than a side effect.

                        The badge says what KIND of manager. The next column
                        says what they COVER. Colby's reasoning, and it is
                        about a role that does not exist yet: "in case we ever
                        have something like an operations manager come into
                        existence which in that case they would need the
                        regular manager badge and the all departments listed in
                        the department column."

                        Kept apart, that person is a new COMBINATION of two
                        signals the list already draws — an ordinary manager
                        badge beside "All departments" — and this table needs
                        no change when they arrive. Collapsed into one cell it
                        would have needed rewriting.

                        The bug this fixes: a branch manager has no department
                        and no station, so both fell through to a dash. The
                        list is the one place you would go to check who runs a
                        branch, and it was the one place that did not say —
                        which reads exactly like the pre-78n4 app, where the
                        empty cell WAS the answer. */}
                    <td>
                      <span className={`badge ${u.role === 'manager' && u.manager_scope === 'branch'
                        ? 'badge-branchmgr'
                        : (ROLE_BADGE[u.role] || 'badge-spare')}`}>
                        {u.role === 'manager' && u.manager_scope === 'branch'
                          ? 'branch manager'
                          : u.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{u.branches?.name || <span className="text-muted">All locations</span>}</td>
                    <td className="text-muted">
                      {u.role === 'manager' && u.manager_scope === 'branch'
                        ? 'All departments'
                        : ([u.department, u.station_truck].filter(Boolean).join(' · ') || '—')}
                    </td>
                    <td>
                      <span className={`badge ${u.archived_at ? 'badge-retired' : u.is_active ? 'badge-active' : 'badge-retired'}`}>
                        {u.archived_at ? 'Archived' : u.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {/* #381, gate callout I and J. The archived row says how
                          many records still carry the name, and who archived
                          them — the answer to "is it safe that this person is
                          gone", standing on the screen rather than needing a
                          query. */}
                      {u.archived_at && (
                        <div className="text-muted" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>
                          {new Date(u.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {archiveEvents[u.id]?.performed_by_name_snapshot
                            ? ` by ${archiveEvents[u.id].performed_by_name_snapshot}` : ''}
                          {typeof archiveEvents[u.id]?.attached_total === 'number'
                            // #705 -- "1 records keep their name" read wrong.
                            ? ` · ${archiveEvents[u.id].attached_total} ${archiveEvents[u.id].attached_total === 1 ? 'record keeps' : 'records keep'} their name` : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {u.archived_at ? (
                          isAdmin && (
                            <button className="btn btn-ghost btn-sm" onClick={() => setRemoveTarget(u)}>
                              Restore
                            </button>
                          )
                        ) : (
                          <>
                            {/* #811, Session 296. Edit and Deactivate only where
                                the database (#798) would accept the press — the
                                screen's copy of its rule, in permissions.js.
                                Send password link stays for everyone: it emails
                                the person their own reset link, which anyone can
                                already ask for from the sign-in page. */}
                            {canChangePerson(viewer, u) && (
                              <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>Edit</button>
                            )}
                            <button className="btn btn-ghost btn-sm" onClick={() => sendPasswordLink(u)}>
                              Send password link
                            </button>
                            {canSetPersonActive(viewer, u) && (
                              <button
                                className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-ghost'}`}
                                onClick={() => toggleActive(u)}
                              >
                                {u.is_active ? 'Deactivate' : 'Reactivate'}
                              </button>
                            )}
                            {/* #381. Admin only — gate question 5. It is the one
                                irreversible action in the app and the set of
                                people who need it is small. A person cannot
                                remove themselves; the server refuses it too,
                                because a hidden button is not a control. */}
                            {isAdmin && String(u.id) !== String(profile?.id) && (
                              <button className="btn btn-ghost btn-sm" onClick={() => setRemoveTarget(u)}>
                                Remove
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {removeTarget && (
        <RemoveUserSheet
          user={removeTarget}
          isMobile={isMobile}
          onClose={() => setRemoveTarget(null)}
          onDone={(out) => {
            setRemoveTarget(null);
            const verb =
              out.action === 'deleted' ? 'deleted permanently'
              : out.action === 'archived' ? 'archived — their access has ended'
              : out.action === 'restored' ? 'restored as inactive'
              : 'updated';
            userMsg.showSuccess(`${out.full_name || 'That person'} ${verb}.`);
            if (out.action === 'archived') setTab('archived');
            load();
          }}
        />
      )}

      {!loading && (
        <StationGovernance
          auth={{ isAdmin, isManager, isExecutive, profile }}
          branches={branches}
          departments={departments}
          onChanged={load}
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">
                {emailConfirm ? 'Change sign-in address' : editUser ? 'Edit user' : 'Invite user'}
              </span>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <InlineMessage type={userMsg.type} message={userMsg.message} onDismiss={userMsg.clearMessage} />

              {/* #404, Session 78n6. The confirmation replaces the form rather
                  than stacking a second overlay on top of it — one surface, one
                  question, and no `position: fixed` anywhere near it.
                  Gate callouts A and B: it names what the person will
                  experience, and it answers the password question before it is
                  asked, because that is the first thing anybody worries about. */}
              {emailConfirm ? (
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                  <div style={{
                    padding: '13px 15px', borderRadius: 8,
                    background: 'var(--off-white)', border: '1px solid var(--gray-ln, #E3E3DF)',
                    marginBottom: 14,
                  }}>
                    <div className="text-muted" style={{ fontSize: 12, marginBottom: 3 }}>Signs in today with</div>
                    <div style={{ marginBottom: 10, wordBreak: 'break-all' }}>{emailConfirm.from || '—'}</div>
                    <div className="text-muted" style={{ fontSize: 12, marginBottom: 3 }}>Will sign in with</div>
                    <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{emailConfirm.to}</div>
                  </div>
                  <p style={{ margin: '0 0 8px' }}>
                    <strong>{emailConfirm.name}</strong> will sign in with <strong>{emailConfirm.to}</strong> from
                    now on, and notifications will go there instead.
                  </p>
                  <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
                    Their password is unchanged and their history stays with them. Nothing is emailed to
                    either address.
                  </p>
                </div>
              ) : (
              <>
              {!editUser && (
                <div className="alert alert-info" style={{ marginBottom: '1rem', fontSize: 13 }}>
                  An invite email will be sent. The user sets their own password on first login.
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full name *</label>
                  <input
                    className="form-input"
                    value={form.full_name}
                    onChange={e => f('full_name', e.target.value)}
                    placeholder="e.g. John Smith"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  {/* #404, Session 78n6. This was `disabled={!!editUser}` and
                      greyed out, so a mistyped invite address was permanent.
                      It is now editable, and the save routes an actual change
                      through a Pages Function that moves the sign-in record and
                      the profile together — see applyEmailChange above. */}
                  <input
                    className="form-input"
                    type="email"
                    value={form.email}
                    onChange={e => f('email', e.target.value)}
                    placeholder="e.g. name@example.com"
                  />
                  {editUser && (
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 5, lineHeight: 1.45 }}>
                      This is the address they sign in with and where their notifications go.
                      Changing it changes both.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-select" value={form.role} onChange={e => f('role', e.target.value)}>
                    {availableRoles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Branch {!['executive', 'purchasing'].includes(form.role) ? '*' : ''}
                  </label>
                  <select
                    className="form-select"
                    value={form.branch_id}
                    onChange={e => f('branch_id', e.target.value)}
                    disabled={isManager && !isAdmin}
                    style={isManager && !isAdmin ? { background: 'var(--off-white)', color: 'var(--gray-lt)' } : {}}
                  >
                    <option value="">
                      {['executive', 'purchasing'].includes(form.role) ? 'All locations' : 'Select branch'}
                    </option>
                    {availableBranches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                {showDepartment && (
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <select
                      className="form-select"
                      value={form.department_choice}
                      onChange={e => f('department_choice', e.target.value)}
                    >
                      {/* #272, Session 78n4. "All departments (branch manager)"
                          used to BE the empty placeholder, so choosing it and
                          never touching the box saved the identical value. The
                          branch-manager answer is now a real option storing a
                          real value, and the placeholder is an unanswered
                          question that the save path rejects for a manager. */}
                      <option value="">
                        {form.role === 'manager' ? 'Choose scope...' : 'Select department'}
                      </option>
                      {form.role === 'manager' && (canSetBranchScope || form.department_choice === 'branch') && (
                        <option value="branch" disabled={!canSetBranchScope}>
                          All departments (branch manager)
                        </option>
                      )}
                      {departments.map(d => (
                        <option key={d.id} value={String(d.id)}>{d.name}</option>
                      ))}
                    </select>
                    {form.role === 'manager' && (
                      <div style={{ fontSize: '12px', color: 'var(--gray-lt)', marginTop: '5px', lineHeight: 1.4 }}>
                        {form.department_choice === 'branch'
                          ? 'Approves every material request at this branch, and cannot be switched off.'
                          : form.department_choice
                            ? `Approves ${selectedDeptName} requests only, once the switch below is on. The branch manager also sees them.`
                            : 'This decides which requests they approve.'}
                      </div>
                    )}
                  </div>
                )}

                {showStation && (
                  <div className="form-group">
                    <label className="form-label">Station / Truck</label>
                    <select
                      className="form-select"
                      value={form.station_truck}
                      onChange={e => f('station_truck', e.target.value)}
                    >
                      <option value="">Select station</option>
                      {availableStations.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {editUser && (
                  <div className="form-group full">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={e => f('is_active', e.target.checked)}
                      />
                      Account active
                    </label>
                  </div>
                )}

                {/* #396, Session 78n4 — approving material requests.
                    Deliberately NOT inside the "Permission overrides" block
                    below, because that block is gated on admin-or-executive and
                    this one is not. Colby's instruction at this session's open:
                    a branch manager changes the approval setting for their own
                    department managers. The other three switches are company-
                    level money-path capabilities and stay with leadership; this
                    one is a branch manager handing down a piece of their own
                    authority, and routing it through a support request would
                    mean it never gets used. */}
                {/* #490, Session 79. The `editUser &&` gate that stood here made
                    this switch render only when editing somebody who already
                    existed — while the hint under the department select, which
                    carries no such condition, told the administrator on the
                    INVITE form to turn on "the switch below". There was no
                    switch below. Every department manager created at go-live
                    landed unable to approve, with nothing erroring and the
                    person who made them believing otherwise. Colby's call
                    between the two candidate fixes: put the switch on the form
                    rather than soften the sentence. */}
                {form.role === 'manager' && canGrantApproval && (
                  <div className="form-group full">
                    <label className="form-label">Material request approval</label>
                    <label style={{
                      display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px',
                      cursor: form.department_choice === 'branch' ? 'default' : 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={form.department_choice === 'branch' ? true : !!form.can_approve_requests}
                        disabled={form.department_choice === 'branch'}
                        onChange={e => f('can_approve_requests', e.target.checked)}
                        style={{ marginTop: '2px' }}
                      />
                      <span>
                        Can approve material requests
                        {form.department_choice === 'branch' && (
                          <span style={{ display: 'block', color: 'var(--gray-lt)', fontSize: '12px', marginTop: '2px' }}>
                            Always on for a branch manager. Every request at this branch reaches
                            them, including any department with no manager assigned — which is what
                            stops a request being raised that nobody can approve.
                          </span>
                        )}
                        {form.department_choice !== 'branch' && (
                          <span style={{ display: 'block', color: 'var(--gray-lt)', fontSize: '12px', marginTop: '2px' }}>
                            {form.can_approve_requests
                              ? `Approves ${selectedDeptName || 'their department'}'s requests. Cannot approve another department's.`
                              : 'Off means they hold the manager role without approving spending — which is the point for a manager who has the role only for Tools.'}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                {/* #611, Session 79.22. team_member ADDED. The sign-off checkbox
                    below has said `form.role === 'team_member'` since #553 D5, and
                    this gate made that condition UNREACHABLE — so the one person
                    whose job it is to sign off a production run, the fabricator,
                    could never be granted it, while permissions.js grants it to a
                    team member by design and the save path writes it for every
                    role. Three of the four parts were built for a team member;
                    only this container was never opened to one. Found performing
                    acceptance line S2, which asks for exactly that switch.

                    THE OBVIOUS FIX WAS WRONG AND WOULD HAVE BEEN WORSE THAN THE
                    DEFECT. Two checkboxes in this block carried no role condition
                    at all, so admitting a team member here alone would have handed
                    every team member vendor-catalog pricing AND invoice
                    reconciliation. They are gated below to match the save path,
                    which writes those two for `manager` and `purchasing` only
                    while writing sign-off for everyone. The rule this file already
                    states twice: what renders must match what saves, or the column
                    is set, ignored and unreachable (#289) — or worse, a control
                    changes something nobody meant to grant. */}
                {editUser && ['manager', 'purchasing', 'team_member'].includes(form.role) && canManagePermissionToggles && (
                  <div className="form-group full">
                    <label className="form-label">Permission overrides</label>
                    {form.role === 'manager' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={form.purchasing_actions_enabled}
                          onChange={e => f('purchasing_actions_enabled', e.target.checked)}
                        />
                        Can act in Purchasing Queue (mark ordered, etc.)
                      </label>
                    )}
                    {/* #422, Session 78t. Renders ONLY for a branch manager,
                        because that is the only person the permission reads it
                        for — canGovernCatalogItem grants admins and executives
                        on their own terms and never reaches this column, and a
                        department manager is now outside the rule entirely. A
                        checkbox that changes nothing for the person in front of
                        you is worse than an absent one: it reads as governance
                        and is not. */}
                    {form.role === 'manager' && form.department_choice === 'branch' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={form.catalog_actions_enabled}
                          onChange={e => f('catalog_actions_enabled', e.target.checked)}
                        />
                        Can govern the item catalog (add, edit, promote and import items)
                      </label>
                    )}
                    {form.role === 'manager' && form.department_choice === 'branch' && !form.catalog_actions_enabled && (
                      <div style={{
                        marginBottom: '6px', padding: '9px 11px', borderRadius: '6px',
                        background: '#F4F6F8', border: '1px solid #DCE2E8', borderLeft: '3px solid #8A97A3',
                        fontSize: '12px', color: '#48545F', lineHeight: 1.5,
                      }}>
                        With this off, only admins and executives can add, edit,
                        promote or import catalog items. This branch manager keeps
                        everything else, including approving their branch's requests.
                      </div>
                    )}
                    {/* #515 D32, Session 79.8. Manager-only, because
                        canSetProductionCost grants admins and executives on
                        their own terms and never reads this column for them —
                        the same shape as the catalog toggle above, and for the
                        same reason: a checkbox that changes nothing for the
                        person in front of you reads as governance and is not.

                        The helper text names the CONSEQUENCE rather than the
                        capability. "Can set production costs" tells an
                        administrator nothing about what they are handing over;
                        "changing it revalues stock already on the shelf" is the
                        fact they need to weigh. */}
                    {form.role === 'manager' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={form.production_cost_actions_enabled}
                          onChange={e => f('production_cost_actions_enabled', e.target.checked)}
                        />
                        Can set what an item costs to make (revalues stock on hand)
                      </label>
                    )}
                    {/* #553 D5, Session 79.15e. TEAM MEMBERS ONLY at the time --
                        WIDENED AT 79.21b TO DEPARTMENT MANAGERS (#584, below),
                        because only a BRANCH manager signs off by role now. The
                        box stays hidden for branch managers and above, for whom
                        it would change nothing.

                        This is how a FABRICATOR gets it. Colby's instruction was
                        "fabrication or branch manager role" and there is no
                        fabrication role -- the five are locked -- so the switch is
                        the shape that instruction takes.

                        HIS RULING AT 79.15e: branch manager is enough for
                        September and he turns individual people on himself. The
                        column ships false for everyone, which is only safe because
                        managers qualify without it -- #515 is what happens when a
                        capability nobody can reach reads as finished.

                        The helper names the CONSEQUENCE rather than the
                        capability: signing off puts stock on the rack and lets a
                        truck be loaded, which is what an administrator needs to
                        weigh. */}
                    {/* EDITING ONLY, and this is a silent-refusal guard rather
                        than a nicety. `functions/create-user.js` accepts a fixed
                        field list and `production_signoff_enabled` is not in it,
                        so on the Add form this box would take a tick, look
                        satisfied, and save nothing. The Add form defaults to
                        team_member, so it would render there by default.
                        #492, #497 and #499 were three of these in one session:
                        a control that accepts a press and reports nothing. */}
                    {/* #584, Session 79.21b -- AND FOR A DEPARTMENT MANAGER. Only a
                        BRANCH manager signs off by role now, so the fabrication
                        manager -- the person this switch was always for -- gets it
                        here. Hidden for a branch manager, for whom it changes
                        nothing. */}
                    {/* #746 D4, Session 79.22b.6.4.1 -- the condition above,
                        unchanged in meaning, now asked of floorSwitchesApplyTo()
                        (locked rule 8), because a second switch shares it. */}
                    {editUser && floorSwitchesApplyTo(form.role, formScope) && (<>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={form.production_signoff_enabled}
                          onChange={e => f('production_signoff_enabled', e.target.checked)}
                        />
                        Can sign off production runs (puts stock on the rack, allows loading)
                      </label>
                      {/* #746 D4, Session 79.22b.6.4.1 -- "CAN RECORD WELDS". Beside
                          sign-off because it IS sign-off's rule with its own switch
                          (canRecordWelds() is canSignOffProduction() character for
                          character), so it shows for exactly the same people. EDITING
                          ONLY, inside this editing-only block, for the sign-off
                          switch's reason: functions/create-user.js takes a fixed
                          field list, so on the Add form a tick would save nothing.
                          The words are gate 746's copy table as amended for #791 at
                          79.22b.6.3.1 - "recounts welded frames", the button's own
                          name, never the retired "counts what's waiting". */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={!!form.weld_record_enabled}
                          onChange={e => f('weld_record_enabled', e.target.checked)}
                        />
                        Can record welds (records welds and recounts welded frames on the Welding page)
                      </label>
                    </>)}
                    {/* #289, Session 74z2 — moved out of the manager-only block.
                        This toggle was written to the database for purchasing
                        users all along (functions/create-user.js applies the
                        company default to `manager` and `purchasing` alike), but
                        it rendered only for managers and canActionVendorCatalog
                        granted purchasing unconditionally — so the column was
                        set, ignored, and unreachable. There was no way to take
                        vendor-catalog access away from an AP user at all, which
                        is exactly the person it needs taking away from.

                        #611, Session 79.22 — ROLE GATE ADDED, and it is not a
                        narrowing: it restores in the render what the save path
                        has always done. These two are written only for `manager`
                        and `purchasing` (see the write block above), so showing
                        them to the team member this session admitted to the
                        container would have been a control that grants nothing
                        — or, far worse if the write block were ever widened to
                        match, vendor pricing and invoice approval handed to the
                        whole shop floor. */}
                    {['manager', 'purchasing'].includes(form.role) && (<>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '6px' }}>
                      <input
                        type="checkbox"
                        checked={form.vendor_catalog_actions_enabled}
                        onChange={e => f('vendor_catalog_actions_enabled', e.target.checked)}
                      />
                      Can edit Vendor Catalog (pricing, contact info, lock/deactivate)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={form.invoice_reconciliation_enabled}
                        onChange={e => f('invoice_reconciliation_enabled', e.target.checked)}
                      />
                      Can reconcile invoices (enter invoice price, resolve price discrepancies)
                    </label>
                    {/* Warn rather than block. An executive can still grant both
                        deliberately — SAP treats this as a conflict to be
                        reviewed and mitigated, not one the system forbids — but
                        it must not be granted without anyone noticing, which is
                        how it was granted to every purchasing account before
                        this session. */}
                    {form.vendor_catalog_actions_enabled && form.invoice_reconciliation_enabled && (
                      <div style={{
                        marginTop: '10px', padding: '9px 11px', borderRadius: '6px',
                        background: '#FFF8EC', border: '1px solid #F3D9A8', borderLeft: '3px solid #E0A500',
                        fontSize: '12px', color: '#6B4A08', lineHeight: 1.5,
                      }}>
                        <strong>Segregation of duties:</strong> this user can create a vendor and
                        also approve invoices billed by that vendor. Grant both only deliberately.
                      </div>
                    )}
                    </>)}
                  </div>
                )}
                {/* #699, gate 665 D28, Session 79.22b.5.1 -- WHO DRIVES. Its own
                    group rather than under Permission overrides, because it
                    grants nothing: it puts the person first on the "Who is
                    driving?" list and, once anybody at their branch is switched
                    on, makes To load and To collect their job and nobody
                    else's. Shown for the roles the driver list offers (admin,
                    manager, team member); an executive or purchasing user is
                    never on that list, so the box would change nothing for
                    them. EDITING ONLY, for the sign-off switch's reason:
                    functions/create-user.js accepts a fixed field list, so on
                    the Add form a tick would look saved and save nothing. */}
                {editUser && ['admin', 'manager', 'team_member'].includes(form.role) && (
                  <div className="form-group full">
                    <label className="form-label">Transfers</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={!!form.drives_trucks}
                        onChange={e => f('drives_trucks', e.target.checked)}
                      />
                      Drives trucks for transfers (listed first as a driver; pickups and loads count as their work)
                    </label>
                  </div>
                )}
              </div>
              </>
              )}
            </div>
            <div className="modal-footer">
              {emailConfirm ? (
                <>
                  <button className="btn btn-ghost" onClick={() => setEmailConfirm(null)} disabled={saving}>
                    Back
                  </button>
                  <button className="btn btn-primary" onClick={saveUser} disabled={saving}>
                    {saving ? 'Changing...' : 'Change it'}
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveUser} disabled={saving}>
                    {saving ? 'Saving...' : editUser ? 'Save changes' : 'Send invite'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}